import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useChartDateRange } from 'hooks';
import './CallStatusDonut.scss';

interface CallStatusData {
    status?: string;
    date?: string;
}

interface CallStatusDistributionBarGraphProps {
    data?: CallStatusData[];
    title?: string;
    amountofCalls?: number;
    height?: number;
    dateRangeType?: string;
    startDate?: Date;
    endDate?: Date;
}

interface StatusGroup {
    name: string;
    value: number;
    color: string;
}

interface TimeGroup extends Record<string, number | string> {
    name: string;
    total: number;
    sortKey: string;
}

interface CustomTooltipProps {
    active?: boolean;
    payload?: Array<{
        dataKey: string;
        value: number;
        color: string;
    }>;
    label?: string;
}

/**
 * A bar graph displaying call status distribution by status or time period
 */
function CallStatusDistributionBarGraph({
    data = [],
    title = 'Call Status Distribution',
    amountofCalls = 0,
    height = 100,
    dateRangeType = 'today',
    startDate,
    endDate,
}: CallStatusDistributionBarGraphProps): React.JSX.Element | null {
    // Use the shared chart date range hook
    const {
        dateRangeDisplay,
        shouldGroupByType,
        groupingStrategy,
        getTimeGroupInfo,
        initializeTimeGroups,
        isStatusView,
    } = useChartDateRange({ dateRangeType, startDate, endDate });

    // Define color palette for different call statuses
    const statusColors: Record<string, string> = {
        transfer_completed: '#4CAF50',
        transfer_failed: '#F44336',
        transfer_in_progress: '#FF9800',
        transfer_user_abandoned: '#FF5722',
        no_agents_available: '#9C27B0',
        agent_rejected: '#A8A812',
        human_on_call: '#2196F3',
        user_abandoned: '#FFA500',
        voicemail: '#607D8B',
        busy: '#795548',
        no_answer: '#E91E63',
        completed: '#8BC34A',
        failed: '#D32F2F',
        cancelled: '#9E9E9E',
        scheduled: '#00BCD4',
        in_progress: '#FFC107',
    };

    // Get all unique statuses from the data
    const allStatuses = useMemo(() => {
        const statusSet = new Set<string>();
        data.forEach((call) => {
            if (call.status) {
                statusSet.add(call.status);
            }
        });
        return Array.from(statusSet);
    }, [data]);

    // Normalize status name for data keys
    const normalizeStatusName = (status: string): string => {
        return status.toLowerCase().replace(/_/g, '_');
    };

    // Get display name for status
    const getStatusDisplayName = (status: string): string => {
        return status
            .toLowerCase()
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    // Generate color for status
    const getStatusColor = (status: string, index?: number): string => {
        const normalized = normalizeStatusName(status);
        if (statusColors[normalized]) {
            return statusColors[normalized];
        }

        // Generate a consistent color based on the status string
        const hash = status.split('').reduce((a, b) => {
            a = (a << 5) - a + b.charCodeAt(0);
            return a & a;
        }, 0);

        // Convert hash to a color
        const hue = Math.abs(hash) % 360;
        return `hsl(${hue}, 65%, 50%)`;
    };

    // Process the raw API data based on date range type
    const processedData = useMemo(() => {
        if (!data || data.length === 0) {
            return [];
        }

        if (shouldGroupByType) {
            // Group by status categories (dynamically based on actual data)
            const statusGroups: Record<string, StatusGroup> = {};

            data.forEach((call) => {
                if (!call.status) return;

                const statusKey = normalizeStatusName(call.status);
                const displayName = getStatusDisplayName(call.status);

                if (!statusGroups[statusKey]) {
                    statusGroups[statusKey] = {
                        name: displayName,
                        value: 0,
                        color: getStatusColor(call.status),
                    };
                }

                statusGroups[statusKey].value += 1;
            });

            // Convert to array and sort by value (highest first)
            return Object.values(statusGroups).sort((a, b) => b.value - a.value);
        } else {
            // Group by time periods for longer date ranges
            const timeGroups: Record<string, TimeGroup> = {};
            const initializeGroup = initializeTimeGroups(allStatuses, normalizeStatusName);

            data.forEach((call) => {
                if (!call.date) return;

                const timeGroupInfo = getTimeGroupInfo(call.date, groupingStrategy);
                if (!timeGroupInfo) return;

                const { groupKey, displayName } = timeGroupInfo;

                if (!timeGroups[groupKey]) {
                    timeGroups[groupKey] = initializeGroup(groupKey, displayName) as TimeGroup;
                }

                timeGroups[groupKey].total += 1;

                // Count each status
                if (call.status) {
                    const normalized = normalizeStatusName(call.status);
                    timeGroups[groupKey][normalized] = ((timeGroups[groupKey][normalized] as number) || 0) + 1;
                }
            });

            // Convert to array and sort by date
            return Object.values(timeGroups).sort((a, b) => a.sortKey.localeCompare(b.sortKey));
        }
    }, [
        data,
        dateRangeType,
        startDate,
        endDate,
        allStatuses,
        shouldGroupByType,
        groupingStrategy,
        getTimeGroupInfo,
        initializeTimeGroups,
    ]);

    if (!data || data.length === 0) {
        return null;
    }

    // Custom tooltip for time-based view
    const CustomTooltip = ({ active, payload, label }: CustomTooltipProps): React.JSX.Element | null => {
        if (active && payload && payload.length) {
            return (
                <div
                    style={{
                        backgroundColor: '#fff',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        padding: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    }}
                >
                    <p style={{ margin: '0 0 8px', fontWeight: 'bold' }}>{`${label}`}</p>
                    {payload.map((entry, index) => (
                        <p key={index} style={{ margin: '8px 0', color: entry.color }}>
                            {`${getStatusDisplayName(entry.dataKey.toUpperCase())}: ${entry.value}`}
                        </p>
                    ))}
                    <p
                        style={{
                            fontWeight: 'bold',
                            borderTop: '1px solid #eee',
                            paddingTop: '8px',
                        }}
                    >
                        {`Total: ${payload.reduce((sum, entry) => sum + entry.value, 0)}`}
                    </p>
                </div>
            );
        }
        return null;
    };

    return (
        <div className='metric-card'>
            <div className='metric-header'>
                <h3>{title}</h3>
                <span className='metric-value'>{`${amountofCalls} calls`}</span>
                {dateRangeDisplay && (
                    <div className='date-range-display' style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                        {dateRangeDisplay}
                    </div>
                )}
            </div>
            <div className='chart-wrapper'>
                <ResponsiveContainer width='100%' height='100%'>
                    <BarChart data={processedData} margin={{ top: 5, right: 5, left: 20, bottom: 5 }}>
                        <XAxis
                            dataKey='name'
                            axisLine={false}
                            tickLine={false}
                            interval={0}
                            tick={{ fontSize: 10 }}
                            angle={isStatusView ? 0 : -45}
                            textAnchor={isStatusView ? 'middle' : 'end'}
                            height={isStatusView ? 30 : 60}
                        />
                        <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10 }} width={30} />

                        {isStatusView ? (
                            <Tooltip
                                formatter={(value) => [value, 'Calls']}
                                labelFormatter={(label) => `${label}:`}
                                wrapperStyle={{ zIndex: 1000 }}
                                contentStyle={{
                                    textAlign: 'left',
                                    backgroundColor: '#fff',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    padding: '8px',
                                }}
                            />
                        ) : (
                            <Tooltip content={<CustomTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                        )}

                        {isStatusView ? (
                            <Bar dataKey='value' fill='#8884d8' radius={[2, 2, 0, 0]} />
                        ) : (
                            allStatuses.map((status, index) => {
                                const normalized = normalizeStatusName(status);
                                const color = getStatusColor(status, index);

                                return (
                                    <Bar
                                        key={normalized}
                                        dataKey={normalized}
                                        fill={color}
                                        radius={[2, 2, 0, 0]}
                                        name={getStatusDisplayName(status)}
                                    />
                                );
                            })
                        )}

                        {!isStatusView && <Legend />}
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

export default CallStatusDistributionBarGraph;
